// seoMeta.js
// Table centrale : un titre + une meta description uniques par page, dans les 6 langues
// supportées par l'app (voir LANGUAGES dans translations.js). Repli sur le français si
// la langue courante n'a pas d'entrée (même logique que t() dans i18n.js).
//
// Utilisation : getSeoMeta({ view, legalPage, companyPage, resourcePage, authMode, selectedMarket, lang })

const SITE_NAME = "EVPTrading";

function pick(entry, lang) {
  return { title: entry.title[lang] ?? entry.title.fr, description: entry.description[lang] ?? entry.description.fr };
}

const DEFAULT_META = {
// Version complète (pour la balise <title> de votre site web)
title: {
  fr: "EVPTrading — Simulation & Marché de prédiction gratuit",
  en: "EVPTrading — Free Simulation & Prediction Market",
  es: "EVPTrading — Simulación y mercado de predicciones gratis",
  de: "EVPTrading — Kostenlose Simulation & Vorhersagemarkt",
  pt: "EVPTrading — Simulação e mercado de predição grátis",
  ar: "محاكاة وسوق توقعات مجاني"
},
description: {
  fr: "EVPTrading est un jeu de simulation et de marché de prédiction utilisant une monnaie virtuelle (EVP). Aucun dépôt d'argent réel n'est requis ni accepté.",
  en: "EVPTrading is a simulation game and prediction market using a virtual currency (EVP). No real money deposit is required or accepted.",
  es: "EVPTrading es un juego de simulación y mercado de predicciones que utiliza una moneda virtual (EVP). No se requiere ni se acepta ningún depósito de dinero real.",
  de: "EVPTrading ist ein Simulationsspiel und Vorhersagemarkt, der eine virtuelle Währung (EVP) nutzt. Es ist keine Einzahlung von echtem Geld erforderlich oder akzeptiert.",
  pt: "EVP é um jogo de simulação e mercado de predição que utiliza uma moeda virtual (EVP). Nenhum depósito em dinheiro real é necessário ou aceito.",
  ar: "EVPTrading هي لعبة محاكاة وسوق توقعات تستخدم عملة افتراضية (EVP). لا يلزم أو يُقبل أي إيداع بأموال حقيقية."
},
};

const AUTH_META = {
  login: {
    title: {
      fr: `Connexion — ${SITE_NAME}`,
      en: `Log In — ${SITE_NAME}`,
      es: `Iniciar sesión — ${SITE_NAME}`,
      de: `Anmelden — ${SITE_NAME}`,
      pt: `Entrar — ${SITE_NAME}`,
      ar: `تسجيل الدخول — ${SITE_NAME}`,
    },
    description: {
      fr: "Connectez-vous à votre compte EVPTrading pour trader sur les marchés prédictifs et gérer votre portefeuille.",
      en: "Log in to your EVPTrading account to trade on prediction markets and manage your wallet.",
      es: "Inicia sesión en tu cuenta de EVPTrading para operar en mercados de predicción y gestionar tu monedero.",
      de: "Melde dich bei deinem EVPTrading-Konto an, um auf Prognosemärkten zu handeln und dein Wallet zu verwalten.",
      pt: "Entre na sua conta EVPTrading para negociar em mercados de previsão e gerenciar sua carteira.",
      ar: "سجّل الدخول إلى حسابك في EVPTradingللتداول في أسواق التنبؤ وإدارة محفظتك.",
    },
  },
  signup: {
    title: {
      fr: `Créer un compte — ${SITE_NAME}`,
      en: `Sign Up — ${SITE_NAME}`,
      es: `Crear cuenta — ${SITE_NAME}`,
      de: `Konto erstellen — ${SITE_NAME}`,
      pt: `Criar conta — ${SITE_NAME}`,
      ar: `إنشاء حساب — ${SITE_NAME}`,
    },
    description: {
      fr: "Créez votre compte EVPTrading gratuitement et commencez à trader sur des événements réels dès aujourd'hui.",
      en: "Create your free EVPTrading account and start trading on real-world events today.",
      es: "Crea tu cuenta gratuita en EVPTrading y empieza a operar en eventos reales hoy mismo.",
      de: "Erstelle dein kostenloses EVPTrading-Konto und handle noch heute mit realen Ereignissen.",
      pt: "Crie sua conta gratuita na EVPTrading e comece a negociar em eventos reais hoje mesmo.",
      ar: "أنشئ حسابك المجاني في EVPTradingوابدأ التداول على أحداث حقيقية اليوم.",
    },
  },
};

const LEGAL_META = {
  cgu: {
    title: {
      fr: `Conditions générales d'utilisation — ${SITE_NAME}`,
      en: `Terms of Service — ${SITE_NAME}`,
      es: `Términos y condiciones — ${SITE_NAME}`,
      de: `Allgemeine Geschäftsbedingungen — ${SITE_NAME}`,
      pt: `Termos de uso — ${SITE_NAME}`,
      ar: `الشروط والأحكام — ${SITE_NAME}`,
    },
    description: {
      fr: "Conditions générales d'utilisation de la plateforme EVPTrading.",
      en: "Terms of service for the EVPTrading platform.",
      es: "Términos y condiciones de la plataforma EVPTrading.",
      de: "Allgemeine Geschäftsbedingungen der EVPTrading-Plattform.",
      pt: "Termos de uso da plataforma EVPTrading.",
      ar: "الشروط والأحكام الخاصة بمنصةEVPTrading.",
    },
  },
  privacy: {
    title: {
      fr: `Politique de confidentialité — ${SITE_NAME}`,
      en: `Privacy Policy — ${SITE_NAME}`,
      es: `Política de privacidad — ${SITE_NAME}`,
      de: `Datenschutzerklärung — ${SITE_NAME}`,
      pt: `Política de privacidade — ${SITE_NAME}`,
      ar: `سياسة الخصوصية — ${SITE_NAME}`,
    },
    description: {
      fr: "Comment EVPTrading collecte, utilise et protège vos données personnelles.",
      en: "How EVPTrading collects, uses, and protects your personal data.",
      es: "Cómo EVPTrading recopila, usa y protege tus datos personales.",
      de: "Wie EVPTrading deine persönlichen Daten erhebt, nutzt und schützt.",
      pt: "Como a EVPTrading coleta, usa e protege seus dados pessoais.",
      ar: "كيف تجمع EVPTradingبياناتك الشخصية وتستخدمها وتحميها.",
    },
  },
  legal: {
    title: {
      fr: `Mentions légales — ${SITE_NAME}`,
      en: `Legal Notice — ${SITE_NAME}`,
      es: `Aviso legal — ${SITE_NAME}`,
      de: `Impressum — ${SITE_NAME}`,
      pt: `Aviso legal — ${SITE_NAME}`,
      ar: `إشعار قانوني — ${SITE_NAME}`,
    },
    description: {
      fr: "Mentions légales et informations sur l'éditeur de la plateforme EVPTrading.",
      en: "Legal notice and publisher information for the EVPTrading platform.",
      es: "Aviso legal e información sobre el editor de la plataforma EVPTrading.",
      de: "Impressum und Angaben zum Betreiber der EVPTrading-Plattform.",
      pt: "Aviso legal e informações sobre o editor da plataforma EVPTrading.",
      ar: "إشعار قانوني ومعلومات عن ناشر منصةEVPTrading.",
    },
  },
  cookies: {
    title: {
      fr: `Politique de cookies — ${SITE_NAME}`,
      en: `Cookie Policy — ${SITE_NAME}`,
      es: `Política de cookies — ${SITE_NAME}`,
      de: `Cookie-Richtlinie — ${SITE_NAME}`,
      pt: `Política de cookies — ${SITE_NAME}`,
      ar: `سياسة ملفات تعريف الارتباط — ${SITE_NAME}`,
    },
    description: {
      fr: "Utilisation des cookies sur EVPTrading et gestion de vos préférences.",
      en: "How EVPTrading uses cookies and how to manage your preferences.",
      es: "Uso de cookies en EVPTrading y gestión de tus preferencias.",
      de: "Verwendung von Cookies auf EVPTrading und Verwaltung deiner Einstellungen.",
      pt: "Uso de cookies na EVPTrading e gestão das suas preferências.",
      ar: "استخدام ملفات تعريف الارتباط في EVPTradingوإدارة تفضيلاتك.",
    },
  },
  rewards: {
    title: {
      fr: `Programme de récompenses — ${SITE_NAME}`,
      en: `Rewards Program — ${SITE_NAME}`,
      es: `Programa de recompensas — ${SITE_NAME}`,
      de: `Prämienprogramm — ${SITE_NAME}`,
      pt: `Programa de recompensas — ${SITE_NAME}`,
      ar: `برنامج المكافآت — ${SITE_NAME}`,
    },
    description: {
      fr: "Découvrez le programme de récompenses et de parrainage EVPTrading.",
      en: "Discover EVPTrading's rewards and referral program.",
      es: "Descubre el programa de recompensas y referidos de EVPTrading.",
      de: "Entdecke das Prämien- und Empfehlungsprogramm von EVPTrading.",
      pt: "Conheça o programa de recompensas e indicação da EVPTrading.",
      ar: "اكتشف برنامج المكافآت والإحالة فيEVPTrading.",
    },
  },
};

const COMPANY_META = {
  about: {
    title: {
      fr: `À propos — ${SITE_NAME}`,
      en: `About — ${SITE_NAME}`,
      es: `Sobre nosotros — ${SITE_NAME}`,
      de: `Über uns — ${SITE_NAME}`,
      pt: `Sobre nós — ${SITE_NAME}`,
      ar: `من نحن — ${SITE_NAME}`,
    },
    description: {
      fr: "Découvrez la mission d'EVP et l'équipe derrière la plateforme de marchés prédictifs.",
      en: "Discover EVPTrading's mission and the team behind the prediction markets platform.",
      es: "Descubre la misión de EVPTrading y el equipo detrás de la plataforma de mercados de predicción.",
      de: "Erfahre mehr über die Mission von EVPTrading und das Team hinter der Prognosemarkt-Plattform.",
      pt: "Conheça a missão da EVPTrading e a equipe por trás da plataforma de mercados de previsão.",
      ar: "تعرّف على مهمة EVPTradingوالفريق وراء منصة أسواق التنبؤ.",
    },
  },
  careers: {
    title: {
      fr: `Carrières — ${SITE_NAME}`,
      en: `Careers — ${SITE_NAME}`,
      es: `Empleo — ${SITE_NAME}`,
      de: `Karriere — ${SITE_NAME}`,
      pt: `Carreiras — ${SITE_NAME}`,
      ar: `الوظائف — ${SITE_NAME}`,
    },
    description: {
      fr: "Rejoignez l'équipe EVPTrading — postes ouverts et culture d'entreprise.",
      en: "Join the EVPTrading team — open positions and company culture.",
      es: "Únete al equipo de EVPTrading — vacantes abiertas y cultura de empresa.",
      de: "Werde Teil des EVPTrading -Teams — offene Stellen und Unternehmenskultur.",
      pt: "Junte-se à equipe da EVPTrading — vagas abertas e cultura da empresa.",
      ar: "انضم إلى فريق EVPTrading — الوظائف الشاغرة وثقافة الشركة.",
    },
  },
  press: {
    title: {
      fr: `Presse — ${SITE_NAME}`,
      en: `Press — ${SITE_NAME}`,
      es: `Prensa — ${SITE_NAME}`,
      de: `Presse — ${SITE_NAME}`,
      pt: `Imprensa — ${SITE_NAME}`,
      ar: `الصحافة — ${SITE_NAME}`,
    },
    description: {
      fr: "Actualités, communiqués et ressources presse EVPTrading.",
      en: "News, press releases, and media resources from EVPTrading.",
      es: "Noticias, comunicados y recursos de prensa de EVPTrading.",
      de: "Neuigkeiten, Pressemitteilungen und Medienressourcen von EVPTrading.",
      pt: "Notícias, comunicados e recursos de imprensa da EVPTrading.",
      ar: "الأخبار والبيانات الصحفية وموارد الإعلام الخاصة بـEVPTrading.",
    },
  },
  partners: {
    title: {
      fr: `Partenaires — ${SITE_NAME}`,
      en: `Partners — ${SITE_NAME}`,
      es: `Socios — ${SITE_NAME}`,
      de: `Partner — ${SITE_NAME}`,
      pt: `Parceiros — ${SITE_NAME}`,
      ar: `الشركاء — ${SITE_NAME}`,
    },
    description: {
      fr: "Devenez partenaire d'EVP et intégrez nos marchés prédictifs.",
      en: "Become an EVPTrading partner and integrate our prediction markets.",
      es: "Conviértete en socio de EVPTrading e integra nuestros mercados de predicción.",
      de: "Werde EVPTrading-Partner und integriere unsere Prognosemärkte.",
      pt: "Torne-se parceiro da EVPTrading e integre nossos mercados de previsão.",
      ar: "كن شريكًا لـ EVPTradingوادمج أسواق التنبؤ الخاصة بنا.",
    },
  },
  blog: {
    title: {
      fr: `Blog — ${SITE_NAME}`,
      en: `Blog — ${SITE_NAME}`,
      es: `Blog — ${SITE_NAME}`,
      de: `Blog — ${SITE_NAME}`,
      pt: `Blog — ${SITE_NAME}`,
      ar: `المدونة — ${SITE_NAME}`,
    },
    description: {
      fr: "Analyses, tendances et actualités des marchés prédictifs par l'équipe EVPTrading.",
      en: "Analysis, trends, and news on prediction markets from the EVPTrading team.",
      es: "Análisis, tendencias y novedades de los mercados de predicción por el equipo de EVPTrading.",
      de: "Analysen, Trends und Neuigkeiten zu Prognosemärkten vom EVPTrading-Team.",
      pt: "Análises, tendências e notícias sobre mercados de previsão pela equipe EVPTrading.",
      ar: "تحليلات واتجاهات وأخبار أسواق التنبؤ من فريقEVPTrading.",
    },
  },
};

const RESOURCE_META = {
  faq: {
    title: {
      fr: `FAQ — ${SITE_NAME}`,
      en: `FAQ — ${SITE_NAME}`,
      es: `Preguntas frecuentes — ${SITE_NAME}`,
      de: `Häufige Fragen — ${SITE_NAME}`,
      pt: `Perguntas frequentes — ${SITE_NAME}`,
      ar: `الأسئلة الشائعة — ${SITE_NAME}`,
    },
    description: {
      fr: "Questions fréquentes sur le fonctionnement d'EVPTrading, les retraits et le trading.",
      en: "Frequently asked questions about how EVPTrading works, withdrawals, and trading.",
      es: "Preguntas frecuentes sobre el funcionamiento de EVPTrading, retiros y trading.",
      de: "Häufige Fragen zur Funktionsweise von EVPTrading, Auszahlungen und Handel.",
      pt: "Perguntas frequentes sobre o funcionamento da EVPTrading, saques e negociação.",
      ar: "أسئلة شائعة حول آلية عمل EVPTradingوالسحب والتداول.",
    },
  },
  glossary: {
    title: {
      fr: `Glossaire — ${SITE_NAME}`,
      en: `Glossary — ${SITE_NAME}`,
      es: `Glosario — ${SITE_NAME}`,
      de: `Glossar — ${SITE_NAME}`,
      pt: `Glossário — ${SITE_NAME}`,
      ar: `المسرد — ${SITE_NAME}`,
    },
    description: {
      fr: "Comprendre le vocabulaire des marchés prédictifs : probabilités, EVP, résolution, et plus.",
      en: "Understand prediction market vocabulary: probabilities, EVP, resolution, and more.",
      es: "Comprende el vocabulario de los mercados de predicción: probabilidades, EVP, resolución y más.",
      de: "Verstehe das Vokabular der Prognosemärkte: Wahrscheinlichkeiten, EVP, Auflösung und mehr.",
      pt: "Entenda o vocabulário dos mercados de previsão: probabilidades, EVP, resolução e mais.",
      ar: "افهم مفردات أسواق التنبؤ: الاحتمالات، EVP، التسوية، والمزيد.",
    },
  },
  api: {
    title: {
      fr: `API — ${SITE_NAME}`,
      en: `API — ${SITE_NAME}`,
      es: `API — ${SITE_NAME}`,
      de: `API — ${SITE_NAME}`,
      pt: `API — ${SITE_NAME}`,
      ar: `واجهة برمجة التطبيقات — ${SITE_NAME}`,
    },
    description: {
      fr: "Documentation de l'API EVPTrading pour intégrer les marchés prédictifs à vos outils.",
      en: "EVP API documentation to integrate prediction markets into your tools.",
      es: "Documentación de la API de EVPTrading para integrar los mercados de predicción en tus herramientas.",
      de: "EVPTrading-API-Dokumentation zur Integration von Prognosemärkten in deine Tools.",
      pt: "Documentação da API da EVPTrading para integrar os mercados de previsão às suas ferramentas.",
      ar: "توثيق واجهة برمجة تطبيقات EVPTradingلدمج أسواق التنبؤ في أدواتك.",
    },
  },
  status: {
    title: {
      fr: `Statut de la plateforme — ${SITE_NAME}`,
      en: `Platform Status — ${SITE_NAME}`,
      es: `Estado de la plataforma — ${SITE_NAME}`,
      de: `Plattformstatus — ${SITE_NAME}`,
      pt: `Status da plataforma — ${SITE_NAME}`,
      ar: `حالة المنصة — ${SITE_NAME}`,
    },
    description: {
      fr: "État en temps réel des services EVPTrading et historique des incidents.",
      en: "Real-time status of EVPTrading services and incident history.",
      es: "Estado en tiempo real de los servicios de EVPTrading e historial de incidentes.",
      de: "Echtzeit-Status der EVPTrading-Dienste und Vorfallhistorie.",
      pt: "Status em tempo real dos serviços da EVPTrading e histórico de incidentes.",
      ar: "الحالة اللحظية لخدمات EVPTradingوسجل الأعطال.",
    },
  },
  contact: {
    title: {
      fr: `Contact — ${SITE_NAME}`,
      en: `Contact — ${SITE_NAME}`,
      es: `Contacto — ${SITE_NAME}`,
      de: `Kontakt — ${SITE_NAME}`,
      pt: `Contato — ${SITE_NAME}`,
      ar: `اتصل بنا — ${SITE_NAME}`,
    },
    description: {
      fr: "Contactez l'équipe support EVPTrading pour toute question.",
      en: "Contact the EVPTrading support team with any questions.",
      es: "Contacta al equipo de soporte de EVPTrading para cualquier pregunta.",
      de: "Kontaktiere das EVPTrading-Supportteam bei Fragen.",
      pt: "Entre em contato com a equipe de suporte da EVPTrading para qualquer dúvida.",
      ar: "تواصل مع فريق دعم EVPTradingلأي استفسار.",
    },
  },
};

const VIEW_META = {
  markets: DEFAULT_META,
  howto: {
    title: {
      fr: `Comment ça marche — ${SITE_NAME}`,
      en: `How It Works — ${SITE_NAME}`,
      es: `Cómo funciona — ${SITE_NAME}`,
      de: `So funktioniert's — ${SITE_NAME}`,
      pt: `Como funciona — ${SITE_NAME}`,
      ar: `كيف يعمل — ${SITE_NAME}`,
    },
     description : {
      fr: `Apprenez à trader sur EVPTrading en quelques étapes : créez un compte, recevez gratuitement 100 EVP renouvelables en cas de perte, tradez sur des événements réels.`,
      en: `Learn to trade on EVPTrading in a few steps: create an account, get 100 free renewable EVP if you lose, and trade on real-world events.`,
      es: `Aprende a operar en EVPTrading en pocos pasos: crea una cuenta, recibe 100 EVP gratis renovables si pierdes y opera en eventos reales.`,
      de: `Lernen Sie das Handeln auf EVPTrading in wenigen Schritten: Konto erstellen, 100 kostenlose aufladbare EVP bei Verlust erhalten und auf reale Ereignisse wetten.`,
      pt: `Aprenda a negociar no EVPTrading em poucas etapas: crie uma conta, receba 100 EVP grátis renováveis se perder e negocie em eventos reais.`,
      ar: `تعلم التداول على EVPTrading في خطوات بسيطة: أنشئ حساباً، واحصل مجاناً على 100 EVP تتجدد عند الخسارة، وتداول على أحداث واقعية.`,
    },
  },
  support: {
    title: {
      fr: `Support — ${SITE_NAME}`,
      en: `Support — ${SITE_NAME}`,
      es: `Soporte — ${SITE_NAME}`,
      de: `Support — ${SITE_NAME}`,
      pt: `Suporte — ${SITE_NAME}`,
      ar: `الدعم — ${SITE_NAME}`,
    },
    description: {
      fr: "Contactez le support EVPTrading ou suivez vos tickets d'assistance.",
      en: "Contact EVPTrading support or track your support tickets.",
      es: "Contacta al soporte de EVPTrading o revisa tus tickets de asistencia.",
      de: "Kontaktiere den EVPTrading-Support oder verfolge deine Support-Tickets.",
      pt: "Entre em contato com o suporte da EVPTrading ou acompanhe seus tickets.",
      ar: "تواصل مع دعم EVPTradingأو تابع تذاكر الدعم الخاصة بك.",
    },
  },
  wallet: {
    title: {
      fr: `Portefeuille — ${SITE_NAME}`,
      en: `Wallet — ${SITE_NAME}`,
      es: `Monedero — ${SITE_NAME}`,
      de: `Wallet — ${SITE_NAME}`,
      pt: `Carteira — ${SITE_NAME}`,
      ar: `المحفظة — ${SITE_NAME}`,
    },
    description: {
      fr: "Gérez votre solde EVP, vos dépôts et vos retraits sur EVPTrading.",
      en: "Manage your EVP balance, deposits, and withdrawals on EVPTrading.",
      es: "Gestiona tu saldo de EVP, tus depósitos y retiros en EVPTrading.",
      de: "Verwalte dein EVP-Guthaben, Einzahlungen und Auszahlungen auf EVPTrading.",
      pt: "Gerencie seu saldo de EVP, depósitos e saques na EVPTrading.",
      ar: "أدر رصيد EVP الخاص بك وعمليات الإيداع والسحب علىEVPTrading.",
    },
  },
  positions: {
    title: {
      fr: `Mes positions — ${SITE_NAME}`,
      en: `My Positions — ${SITE_NAME}`,
      es: `Mis posiciones — ${SITE_NAME}`,
      de: `Meine Positionen — ${SITE_NAME}`,
      pt: `Minhas posições — ${SITE_NAME}`,
      ar: `مراكزي — ${SITE_NAME}`,
    },
    description: {
      fr: "Consultez vos positions ouvertes et votre historique de trading sur EVPTrading.",
      en: "View your open positions and trading history on EVPTrading.",
      es: "Consulta tus posiciones abiertas y tu historial de operaciones en EVPTrading.",
      de: "Sieh dir deine offenen Positionen und deinen Handelsverlauf auf EVPTrading an.",
      pt: "Veja suas posições abertas e seu histórico de negociação na EVPTrading.",
      ar: "اطّلع على مراكزك المفتوحة وسجل التداول الخاص بك علىEVPTrading.",
    },
  },
};

export function getSeoMeta({ view, legalPage, companyPage, resourcePage, authMode, selectedMarket, lang = "fr" }) {
  // Priorité 1 : la modale connexion/inscription, ouvrable sur n'importe quelle vue
  if (authMode === "login")  return pick(AUTH_META.login, lang);
  if (authMode === "signup") return pick(AUTH_META.signup, lang);

  // Priorité 2 : détail d'un marché — titre dynamique basé sur le marché ouvert
  // (le titre du marché lui-même n'est pas traduit ici : il vient tel quel de l'API,
  // à condition que le backend renvoie déjà `title` dans la langue courante)
  if (view === "markets" && selectedMarket?.title) {
    const TRADE_ON = {
      fr: (t) => `Tradez sur "${t}" et suivez l'évolution des probabilités en temps réel sur EVPTrading.`,
      en: (t) => `Trade on "${t}" and follow real-time probability changes on EVPTrading.`,
      es: (t) => `Opera sobre "${t}" y sigue la evolución de las probabilidades en tiempo real en EVPTrading.`,
      de: (t) => `Handle mit "${t}" und verfolge die Wahrscheinlichkeitsentwicklung in Echtzeit auf EVPTrading.`,
      pt: (t) => `Negocie em "${t}" e acompanhe a evolução das probabilidades em tempo real na EVPTrading.`,
      ar: (t) => `تداول على "${t}" وتابع تطور الاحتمالات في الوقت الفعلي علىEVPTrading.`,
    };
    const fn = TRADE_ON[lang] ?? TRADE_ON.fr;
    return { title: `${selectedMarket.title} — ${SITE_NAME}`, description: fn(selectedMarket.title) };
  }

  // Priorité 3 : sous-pages légales
  if (view === "legal" && LEGAL_META[legalPage]) return pick(LEGAL_META[legalPage], lang);

  // Priorité 4 : sous-pages entreprise
  if (view === "company" && COMPANY_META[companyPage]) return pick(COMPANY_META[companyPage], lang);

  // Priorité 5 : sous-pages ressources
  if (view === "resources" && RESOURCE_META[resourcePage]) return pick(RESOURCE_META[resourcePage], lang);

  // Priorité 6 : vues principales
  if (VIEW_META[view]) return pick(VIEW_META[view], lang);

  // Fallback
  return pick(DEFAULT_META, lang);
}
